import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { formatCLP, formatDate, formatThousands } from "../../lib/format";
import { PaperPlaneTilt, HandCoins, ArrowRight, ArrowUpRight, ArrowDownLeft, Eye, EyeSlash, Bank, LockKey, WarningCircle } from "@phosphor-icons/react";

export default function ClientDashboard() {
  const { user, refreshMe } = useAuth();
  const [txs, setTxs] = useState([]);
  const [loans, setLoans] = useState([]);
  const [hideBalance, setHideBalance] = useState(false);

  useEffect(() => {
    refreshMe();
    api.get("/me/transactions").then((r) => setTxs(r.data.slice(0, 6)));
    api.get("/me/loans").then((r) => setLoans(r.data.slice(0, 3)));
  }, []); // eslint-disable-line

  const loansLocked = !user?.loans_unlocked;

  return (
    <div className="space-y-8">
      <header>
        <div className="label-mono mb-2">Dashboard cliente</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter">
          Hola, <span className="text-forest">{user?.name?.split(" ")[0]}</span>.
        </h1>
      </header>

      {/* Aviso: activar cuenta con primer depósito */}
      {loansLocked && (
        <div
          className="relative overflow-hidden border border-warn/30 bg-warn/5 p-5 md:p-6 flex flex-col sm:flex-row sm:items-center gap-4"
          data-testid="activate-account-banner"
        >
          <span className="absolute left-0 top-0 bottom-0 w-1.5 bg-warn" />
          <div className="w-12 h-12 shrink-0 flex items-center justify-center bg-warn/15 text-warn">
            <WarningCircle size={26} weight="fill" />
          </div>
          <div className="flex-1">
            <h3 className="font-display text-lg font-bold">Activa tu cuenta</h3>
            <p className="text-sm text-inkMuted mt-1">
              Realiza tu <b className="text-ink">primer depósito electrónico</b> para activar tu cuenta vista y
              habilitar las funciones de <b className="text-ink">créditos</b>. Una vez que tu asesor confirme el
              depósito, estas opciones se habilitarán automáticamente.
            </p>
          </div>
        </div>
      )}

      {/* Datos bancarios */}
      <BankDataCard bankData={user?.bank_data} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 border border-hairline bg-surface">
        {/* Balance */}
        <div className="lg:col-span-2 p-8 border-b lg:border-b-0 lg:border-r border-hairline" data-testid="balance-card">
          <div className="flex items-center justify-between mb-6">
            <span className="label-mono">Saldo disponible</span>
            <button onClick={() => setHideBalance((v) => !v)} className="text-inkMuted hover:text-forest" data-testid="toggle-balance">
              {hideBalance ? <EyeSlash size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <div className="font-mono text-5xl md:text-6xl font-medium tracking-tight mb-4" data-testid="balance-amount">
            {hideBalance ? "$ •••••••" : formatCLP(user?.balance)}
          </div>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/app/transferencias" className="h-11 px-5 bg-forest text-bg text-sm font-semibold flex items-center gap-2 hover:bg-forest-hover">
              <PaperPlaneTilt size={16} weight="bold" /> Transferir
            </Link>
            {loansLocked ? (
              <Link
                to="/app/creditos"
                className="h-11 px-5 border border-hairline text-sm font-semibold flex items-center gap-2 text-inkMuted hover:bg-bg"
                title="Activa tu cuenta vista con un primer depósito para habilitar los créditos"
                data-testid="loan-locked-btn"
              >
                <LockKey size={16} weight="bold" /> Pedir crédito
              </Link>
            ) : (
              <Link to="/app/creditos" className="h-11 px-5 border border-hairline text-sm font-semibold flex items-center gap-2 hover:bg-bg">
                <HandCoins size={16} weight="bold" /> Pedir crédito
              </Link>
            )}
          </div>
          {loansLocked && (
            <div className="mt-4 flex items-start gap-2 text-xs text-inkMuted" data-testid="loan-locked-note">
              <LockKey size={14} weight="bold" className="mt-0.5 shrink-0 text-warn" />
              <span>Para solicitar créditos, activa tu cuenta vista con un primer depósito electrónico. Tu asesor habilitará esta opción al confirmarlo.</span>
            </div>
          )}
        </div>

        {/* Summary */}
        <div className="p-8 grid grid-cols-2 gap-6 bg-bg">
          <Stat label="Movimientos" value={txs.length} />
          <Stat label="Créditos activos" value={loans.filter((l) => ["aprobado", "desembolsado"].includes(l.status)).length} />
          <Stat label="Solicitudes" value={loans.filter((l) => l.status === "pendiente").length} />
          <Stat label="Estado" value={<span className="text-success">{user?.status === "active" ? "Activa" : "Bloqueada"}</span>} />
        </div>
      </div>

      {/* Recent movements */}
      <div className="bento">
        <div className="flex items-center justify-between px-6 py-4 border-b border-hairline">
          <h2 className="font-display text-xl font-bold">Últimos movimientos</h2>
          <Link to="/app/movimientos" className="text-sm text-forest hover:text-burnt flex items-center gap-1">
            Ver todos <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
        {txs.length === 0 ? (
          <div className="p-10 text-center text-inkMuted text-sm">Sin movimientos aún.</div>
        ) : (
          <table className="w-full text-sm" data-testid="movements-table">
            <thead>
              <tr className="label-mono">
                <th className="text-left px-6 py-3">Fecha</th>
                <th className="text-left px-6 py-3">Descripción</th>
                <th className="text-left px-6 py-3">Estado</th>
                <th className="text-right px-6 py-3">Monto</th>
              </tr>
            </thead>
            <tbody>
              {txs.map((t) => (
                <tr key={t.id} className="border-t border-hairline hover:bg-bg">
                  <td className="px-6 py-3 font-mono text-xs text-inkMuted">{formatDate(t.created_at)}</td>
                  <td className="px-6 py-3">
                    <div className="flex items-center gap-2">
                      {t.type === "credit" ? (
                        <ArrowDownLeft size={16} weight="bold" className="text-success" />
                      ) : (
                        <ArrowUpRight size={16} weight="bold" className="text-burnt" />
                      )}
                      <span>{t.description}</span>
                    </div>
                  </td>
                  <td className="px-6 py-3 label-mono">{t.status}</td>
                  <td className={`px-6 py-3 text-right font-mono font-medium ${t.type === "credit" ? "text-success" : "text-ink"}`}>
                    {t.type === "credit" ? "+" : "−"} {formatCLP(t.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="label-mono mb-1">{label}</div>
      <div className="font-display text-3xl font-bold">{value}</div>
    </div>
  );
}

function BankDataCard({ bankData }) {
  const enabled = bankData?.enabled;

  if (!enabled) {
    return (
      <div
        className="border border-dashed border-hairline bg-bg p-6 flex items-start gap-4"
        data-testid="bank-data-pending"
      >
        <div className="w-11 h-11 flex items-center justify-center bg-surface border border-hairline shrink-0">
          <Bank size={20} className="text-inkMuted" />
        </div>
        <div>
          <div className="font-display text-xl font-bold">Datos bancarios</div>
          <div className="mt-1 inline-flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-warn animate-pulse" />
            <span className="text-sm text-inkMuted">
              Datos bancarios pendientes de asignación. Tu asesor los habilitará pronto.
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-hairline bg-surface" data-testid="bank-data-card">
      <div className="px-6 py-4 border-b border-hairline flex items-center gap-2">
        <Bank size={18} weight="bold" className="text-forest" />
        <h2 className="font-display text-xl font-bold">Datos bancarios</h2>
        <span className="ml-auto label-mono text-success">Habilitado</span>
      </div>
      <div className="p-6 grid grid-cols-2 md:grid-cols-3 gap-6">
        <BankField label="Nombre" value={bankData.nombre} />
        <BankField label="ID VIRTUAL" value={bankData.rut} mono />
        <BankField label="N° de cuenta" value={bankData.cuenta} mono />
        <BankField label="Tipo de cuenta" value={bankData.tipo_cuenta} />
        <BankField label="Valor" value={formatThousands(bankData.valor)} mono />
      </div>
      {bankData.descripcion ? (
        <div className="px-6 pb-6">
          <div className="label-mono mb-1">Descripción</div>
          <p className="text-sm text-ink whitespace-pre-line bg-bg border border-hairline p-4">{bankData.descripcion}</p>
        </div>
      ) : null}
    </div>
  );
}

function BankField({ label, value, mono = false }) {
  return (
    <div>
      <div className="label-mono mb-1">{label}</div>
      <div className={`font-medium break-words ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
    </div>
  );
}
